import { apiClient } from '@/utils/api-client';
import type {
  ConversionFromBankLinesCreate,
  CurrencyConversionCreateResponse,
  ConversionProposal,
  FundingChain,
  CurrencyConversionCreate,
  CurrencyConversionRead,
} from '@/types/currency-conversion';
import type { ForeignCashPositionsResponse } from '@/types/foreign-cash-positions';
import type { FxPeriodEndPreviewResponse } from '@/types/fx-period-end';

// apiClient turns a null body into {}; the endpoint answers null when there is no chain.
const chainOrNull = (chain: FundingChain): FundingChain | null =>
  chain.steps ? chain : null;

export const currencyConversionService = {
  async create(
    fundId: string,
    payload: CurrencyConversionCreate
  ): Promise<CurrencyConversionRead> {
    return await apiClient.post(
      `/funds/${fundId}/currency-conversions`,
      payload
    );
  },

  async recordFromBankLines(
    fundId: string,
    payload: ConversionFromBankLinesCreate
  ): Promise<CurrencyConversionCreateResponse> {
    return await apiClient.post(
      `/funds/${fundId}/currency-conversions/from-bank-lines`,
      payload
    );
  },

  async listSuggestions(fundId: string): Promise<ConversionProposal[]> {
    return await apiClient.get(
      `/funds/${fundId}/currency-conversions/suggestions`
    );
  },

  async acceptSuggestion(
    fundId: string,
    tradeReference: string
  ): Promise<CurrencyConversionRead> {
    return await apiClient.post(
      `/funds/${fundId}/currency-conversions/suggestions/accept`,
      { trade_reference: tradeReference }
    );
  },

  async deleteDraft(fundId: string, fundTransactionId: string): Promise<void> {
    return await apiClient.delete(
      `/funds/${fundId}/currency-conversions/${fundTransactionId}`
    );
  },

  async getSuggestionFundingChain(
    fundId: string,
    tradeReference: string
  ): Promise<FundingChain | null> {
    const chain = await apiClient.get<FundingChain>(
      `/funds/${fundId}/currency-conversions/suggestions/${encodeURIComponent(
        tradeReference
      )}/funding-chain`
    );
    return chainOrNull(chain);
  },

  async getFundingChain(
    fundId: string,
    fundTransactionId: string
  ): Promise<FundingChain | null> {
    const chain = await apiClient.get<FundingChain>(
      `/funds/${fundId}/currency-conversions/${fundTransactionId}/funding-chain`
    );
    return chainOrNull(chain);
  },

  async previewPeriodEnd(
    fundId: string,
    onDate: string
  ): Promise<FxPeriodEndPreviewResponse> {
    return await apiClient.get(
      `/funds/${fundId}/ledger/fx-period-end/preview?on_date=${onDate}`
    );
  },

  async getPeriodEndWorking(
    fundId: string,
    fundTransactionId: string
  ): Promise<FxPeriodEndPreviewResponse> {
    return await apiClient.get(
      `/funds/${fundId}/ledger/fx-period-end/${fundTransactionId}/working`
    );
  },

  async discardPeriodEnd(
    fundId: string,
    fundTransactionId: string
  ): Promise<void> {
    return await apiClient.delete(
      `/funds/${fundId}/ledger/fx-period-end/${fundTransactionId}`
    );
  },

  async listForeignCashPositions(
    fundId: string
  ): Promise<ForeignCashPositionsResponse> {
    return await apiClient.get(
      `/funds/${fundId}/ledger/foreign-cash-positions`
    );
  },
};
